/**
 * Pan/zoom camera over the world layer.
 *
 * Deliberately free of PixiJS *values* — only the `Container` type is imported,
 * and type imports are erased at compile time. That keeps every line of this
 * file unit-testable in Node with no WebGL context, which matters because the
 * screen<->world transform is the thing every input handler and hit-test in the
 * client depends on being exactly right.
 *
 * Conventions fixed here, once:
 * - `centre` is the **world** point under the middle of the viewport.
 * - `zoom` is screen pixels per world pixel. Larger = closer in.
 * - `panBy` moves the **camera**, not the map. Drag-to-pan therefore passes the
 *   negated pointer delta, since dragging right should move the camera left.
 */

import type { Container } from "pixi.js";
import { clamp } from "kngdm-shared";

export interface MapPixelBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export interface CameraOptions {
  readonly minZoom: number;
  readonly maxZoom: number;
  readonly bounds: MapPixelBounds;
}

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * How far past the map edge the camera may travel, as a fraction of the visible
 * half-extent. A little slack stops the camera feeling like it hits a wall the
 * moment the last hex reaches the screen edge; more than this and the player
 * loses the map off-screen.
 */
/** Zoom applied when `fitBounds` cannot fit the map (degenerate bounds). */
const FALLBACK_ZOOM = 1;

export class Camera {
  private viewWidth: number;
  private viewHeight: number;
  private readonly minZoomValue: number;
  private readonly maxZoomValue: number;
  private readonly boundsValue: MapPixelBounds;

  private zoomValue = FALLBACK_ZOOM;
  private centreX = 0;
  private centreY = 0;

  constructor(viewport: Viewport, options: CameraOptions) {
    if (!(options.minZoom > 0)) {
      throw new Error(`Camera minZoom must be positive, got ${options.minZoom}`);
    }
    if (options.maxZoom < options.minZoom) {
      throw new Error(
        `Camera maxZoom (${options.maxZoom}) must be >= minZoom (${options.minZoom})`,
      );
    }

    this.viewWidth = Math.max(1, viewport.width);
    this.viewHeight = Math.max(1, viewport.height);
    this.minZoomValue = options.minZoom;
    this.maxZoomValue = options.maxZoom;
    this.boundsValue = options.bounds;

    this.fitBounds();
  }

  get zoom(): number {
    return this.zoomValue;
  }

  get centre(): WorldPoint {
    return { x: this.centreX, y: this.centreY };
  }

  get minZoom(): number {
    return this.minZoomValue;
  }

  get maxZoom(): number {
    return this.maxZoomValue;
  }

  get bounds(): MapPixelBounds {
    return this.boundsValue;
  }

  get viewport(): Viewport {
    return { width: this.viewWidth, height: this.viewHeight };
  }

  /** Moves the camera by a screen-space delta. Drag-to-pan negates its input. */
  panBy(dxScreen: number, dyScreen: number): void {
    this.centreX += dxScreen / this.zoomValue;
    this.centreY += dyScreen / this.zoomValue;
    this.clampCentre();
  }

  /**
   * Zooms by `factor` about a screen point, keeping the world point under that
   * point fixed. Anchoring to the cursor rather than the viewport centre is the
   * difference between zoom feeling like a tool and feeling like a fight; the
   * GDD's input map calls for scroll-wheel zoom "toward the cursor position".
   *
   * The invariant holds exactly except when the resulting centre would leave the
   * map bounds, where clamping wins — losing the map is worse than a few pixels
   * of drift at the very edge.
   */
  zoomAt(screenPoint: { x: number; y: number }, factor: number): void {
    if (!Number.isFinite(factor) || factor <= 0) return;

    const target = clamp(this.zoomValue * factor, this.minZoomValue, this.maxZoomValue);
    if (target === this.zoomValue) return;

    const anchor = this.screenToWorld(screenPoint);
    this.zoomValue = target;
    this.centreX = anchor.x - (screenPoint.x - this.viewWidth / 2) / target;
    this.centreY = anchor.y - (screenPoint.y - this.viewHeight / 2) / target;
    this.clampCentre();
  }

  centreOn(world: { x: number; y: number }): void {
    this.centreX = world.x;
    this.centreY = world.y;
    this.clampCentre();
  }

  /** Frames the whole map: the continent end of the GDD's zoom range. */
  fitBounds(): void {
    const mapWidth = this.boundsValue.maxX - this.boundsValue.minX;
    const mapHeight = this.boundsValue.maxY - this.boundsValue.minY;

    const fit =
      mapWidth > 0 && mapHeight > 0
        ? Math.min(this.viewWidth / mapWidth, this.viewHeight / mapHeight)
        : FALLBACK_ZOOM;

    this.zoomValue = clamp(fit, this.minZoomValue, this.maxZoomValue);
    this.centreX = (this.boundsValue.minX + this.boundsValue.maxX) / 2;
    this.centreY = (this.boundsValue.minY + this.boundsValue.maxY) / 2;
    this.clampCentre();
  }

  screenToWorld(p: { x: number; y: number }): WorldPoint {
    return {
      x: (p.x - this.viewWidth / 2) / this.zoomValue + this.centreX,
      y: (p.y - this.viewHeight / 2) / this.zoomValue + this.centreY,
    };
  }

  worldToScreen(p: { x: number; y: number }): WorldPoint {
    return {
      x: (p.x - this.centreX) * this.zoomValue + this.viewWidth / 2,
      y: (p.y - this.centreY) * this.zoomValue + this.viewHeight / 2,
    };
  }

  /** The world-space rectangle currently on screen — the culling input. */
  visibleWorldRect(): { topLeft: WorldPoint; bottomRight: WorldPoint } {
    return {
      topLeft: this.screenToWorld({ x: 0, y: 0 }),
      bottomRight: this.screenToWorld({ x: this.viewWidth, y: this.viewHeight }),
    };
  }

  /**
   * Window resize keeps the world point at the viewport centre where it is, so
   * a resize never teleports the player. Only the clamp can move it, and only
   * when the new viewport shows more map than the bounds allow.
   */
  resize(viewport: Viewport): void {
    this.viewWidth = Math.max(1, viewport.width);
    this.viewHeight = Math.max(1, viewport.height);
    this.clampCentre();
  }

  /** Writes the transform onto the world container. Call once per frame. */
  applyTo(world: Container): void {
    world.scale.set(this.zoomValue);
    world.position.set(
      this.viewWidth / 2 - this.centreX * this.zoomValue,
      this.viewHeight / 2 - this.centreY * this.zoomValue,
    );
  }

  /**
   * Keeps the visible rect inside the map plus an overscroll margin. When the
   * map is smaller than the viewport on an axis it is locked centred on that
   * axis, which is the only stable answer — any clamp range would be empty.
   */
  /**
   * Keep the camera centre inside the map rectangle.
   *
   * The first version of this kept the whole *viewport* inside the map, which
   * reads as tidier — the map always fills the screen — but has a failure the
   * player feels immediately: a kingdom near a map edge can never be brought to
   * the middle of the screen. At the shipped zoom range, a spawn on the western
   * edge of a 40x40 map sat 477 pixels left of centre and could not be moved,
   * because the clamp ran out of travel before the kingdom reached the middle.
   * At *maximum* zoom it still could not be centred.
   *
   * Clamping the centre instead means any hex on the map can be put under the
   * crosshair, at the cost of showing some void past the edge when you go
   * looking for it. That is the right trade for a game where your territory can
   * spawn anywhere and is the thing you most need to look at.
   */
  private clampCentre(): void {
    this.centreX = clamp(this.centreX, this.boundsValue.minX, this.boundsValue.maxX);
    this.centreY = clamp(this.centreY, this.boundsValue.minY, this.boundsValue.maxY);
  }
}
